/**
 * Per-conversation model/thinking resolution for the composer pickers.
 *
 * The lane worker restarts a direct lane's provider session — and replays the
 * whole visible history — whenever a send's (model, effort) differs from the
 * turn that last replied. A single global picker value therefore silently
 * turned an innocent follow-up into a full replay whenever Mark had switched
 * model in some other chat. The pickers now resolve, in order:
 *
 *   1. an explicit pick Mark made in THIS conversation and has not sent yet,
 *   2. what this conversation last ran (`last_turn_runtime` from the API),
 *   3. what the lane last ran before its latest reset (`last_used_runtime`),
 *   4. this device's last pick for the lane (conversations with no turn yet),
 *   5. the provider default.
 *
 * `diverges` flags a pick that differs from the running configuration so the
 * composer can warn before the expensive send.
 */

export type LastTurnRuntime = {
  /** Canonical model id, or null when the turn ran the provider default. */
  model: string | null
  /** Effort/thinking level, or null when the turn ran the provider default. */
  effort: string | null
}

export type RuntimeModelOption = { value: string; label: string }
export type RuntimeEffortOption = { value: string; label: string }

export type RuntimePick = { model?: string; effort?: string }

export type RuntimeTuple = { model: string; effort: string }

export type ResolvedRuntimeSelection = {
  model: string
  effort: string
  /** Effective configuration the conversation last ran; null with no turn yet. */
  lastTurn: RuntimeTuple | null
  /** True when the current selection differs from `lastTurn`. */
  diverges: boolean
  /** True when `lastTurn` can be re-selected with the offered options. */
  canRevert: boolean
}

export type ResolveRuntimeSelectionArgs = {
  /** `undefined` = unknown (older cached row); `null` = conversation has no turn. */
  lastTurn: LastTurnRuntime | null | undefined
  /** Explicit unsent pick for this conversation, if any. */
  pick: RuntimePick | null
  /** Lane's latest explicit runtime across resets; null/undefined = none. */
  lastUsed?: LastTurnRuntime | null
  /** Device's last pick for the lane, applied only with no turn yet. */
  fallback: RuntimeTuple
  /** What a null stored override means at runtime (provider defaults). */
  defaults: RuntimeTuple
  modelOptions: readonly RuntimeModelOption[]
  /** Effort values the given model accepts, in picker order. */
  effortsForModel: (model: string) => readonly string[]
}

function offered(options: readonly RuntimeModelOption[], model: string | null | undefined): model is string {
  return typeof model === 'string' && options.some((option) => option.value === model)
}

function effectiveLastTurn(
  lastTurn: LastTurnRuntime | null | undefined,
  defaults: RuntimeTuple,
): RuntimeTuple | null {
  if (!lastTurn) return null
  return {
    model: lastTurn.model ?? defaults.model,
    effort: lastTurn.effort ?? defaults.effort,
  }
}

export function resolveRuntimeSelection(args: ResolveRuntimeSelectionArgs): ResolvedRuntimeSelection {
  const { pick, fallback, defaults, modelOptions, effortsForModel } = args
  const lastTurn = effectiveLastTurn(args.lastTurn, defaults)
  const lastUsed = effectiveLastTurn(args.lastUsed, defaults)

  const model = [pick?.model, lastTurn?.model, lastUsed?.model, fallback.model, defaults.model]
    .find((candidate) => offered(modelOptions, candidate))
    ?? modelOptions[0]?.value ?? defaults.model

  const allowedEfforts = effortsForModel(model)
  const effort = [pick?.effort, lastTurn?.effort, lastUsed?.effort, fallback.effort, defaults.effort]
    .find((candidate) => typeof candidate === 'string' && allowedEfforts.includes(candidate))
    ?? allowedEfforts[0] ?? defaults.effort

  const diverges = lastTurn !== null && (model !== lastTurn.model || effort !== lastTurn.effort)
  const canRevert = lastTurn !== null
    && offered(modelOptions, lastTurn.model)
    && effortsForModel(lastTurn.model).includes(lastTurn.effort)

  return { model, effort, lastTurn, diverges, canRevert }
}

/** Human label such as "Fable 5.1 · Max" for a runtime tuple. */
export function describeRuntime(
  tuple: RuntimeTuple,
  modelOptions: readonly RuntimeModelOption[],
  effortOptions: readonly RuntimeEffortOption[],
): string {
  const modelLabel = modelOptions.find((option) => option.value === tuple.model)?.label ?? tuple.model
  const effortLabel = effortOptions.find((option) => option.value === tuple.effort)?.label ?? tuple.effort
  return `${modelLabel} · ${effortLabel}`
}

/** Conversation identity for unsent picks: a topic is its own conversation. */
export function runtimeConversationKey(chatId: string, topicId: string | null | undefined): string {
  return `${chatId}::${topicId ?? ''}`
}
